import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, Smartphone } from "lucide-react";
import { motion } from "framer-motion";

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [canal, setCanal] = useState("email");
  const [smsAtivo, setSmsAtivo] = useState(false);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const contato = canal === "email" ? email : phone;

  useEffect(() => {
    api.get("/auth/forgot-password/canais").then((r) => setSmsAtivo(!!r.data.sms)).catch(() => {});
  }, []);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password/send", canal === "email" ? { email } : { phone });
      toast.success(data.message || "Confira seu e-mail.");
      setStep(2);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password/verify", { contato, code });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetPass = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: newPassword });
      toast.success("Senha alterada! Faça login.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Voltar ao login
        </Link>
        <h1 className="font-display text-3xl font-black text-primary">Recuperar senha</h1>
        <p className="text-muted-foreground mt-2 mb-8">Enviaremos um código de verificação para o seu e-mail ou celular cadastrado.</p>

        {step === 1 && (
          <form onSubmit={sendCode} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "email", l: "E-mail", i: Mail }, { v: "sms", l: "SMS", i: Smartphone }].map((o) => {
                const Icon = o.i;
                return (
                  <button type="button" key={o.v} data-testid={`canal-${o.v}`} onClick={() => setCanal(o.v)}
                    className={`flex items-center justify-center gap-2 rounded-xl border-2 font-medium transition-colors ${canal === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"}`}
                    style={{ height: 52 }}>
                    <Icon className="w-4 h-4" /> {o.l}
                  </button>
                );
              })}
            </div>
            {canal === "email" ? (
            <div>
              <Label className="text-muted-foreground">E-mail cadastrado</Label>
              <Input data-testid="forgot-email" type="email" className="mt-1.5 rounded-xl bg-card" style={{ height: 52 }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seuemail@exemplo.com" required />
            </div>
            ) : (
            <div>
              <Label className="text-muted-foreground">Celular cadastrado (com DDD)</Label>
              <Input data-testid="forgot-phone" type="tel" className="mt-1.5 rounded-xl bg-card" style={{ height: 52 }} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 99999-9999" required />
              {!smsAtivo && <p data-testid="sms-inativo" className="text-sm text-amber-400 mt-2">O envio por SMS ainda não está ativo. Por enquanto, use o e-mail.</p>}
            </div>
            )}
            <Button data-testid="forgot-send" disabled={loading} className="w-full rounded-full text-lg font-semibold" style={{ height: 56 }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar código"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyCode} className="space-y-5">
            <div>
              <p data-testid="forgot-check-email" className="text-sm text-muted-foreground mb-3">Enviamos um código de 6 dígitos para <b>{contato}</b>.{canal === "email" ? " Confira também a caixa de spam." : ""}</p>
              <Label className="text-muted-foreground">Código de verificação</Label>
              <Input data-testid="forgot-code" className="mt-1.5 rounded-xl bg-card text-center tracking-[0.5em] text-2xl" style={{ height: 60 }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} required />
            </div>
            <Button data-testid="forgot-verify" disabled={loading} className="w-full rounded-full text-lg font-semibold" style={{ height: 56 }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verificar código"}
            </Button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPass} className="space-y-5">
            <div>
              <Label className="text-muted-foreground">Nova senha</Label>
              <Input data-testid="forgot-newpass" type="password" className="mt-1.5 rounded-xl bg-card" style={{ height: 52 }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Digite a nova senha" required />
            </div>
            <Button data-testid="forgot-reset" disabled={loading} className="w-full rounded-full text-lg font-semibold" style={{ height: 56 }}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salvar nova senha"}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
